// react-scan must load before React to instrument it; dev-only via dynamic import
// enable for diagnosing rendering performance
// if (import.meta.env.DEV) {
//   void import("react-scan").then(({ scan }) => scan({ enabled: true }))
// }

import { StrictMode } from "react"
import ReactDOM from "react-dom/client"
import {
  RouterProvider,
  createRouter,
  createMemoryHistory,
} from "@tanstack/react-router"
import { openDB } from "idb"
import { persistQueryClient } from "@tanstack/react-query-persist-client"
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister"
import "./index.css"
import { PostHogProvider } from "@posthog/react"
import { hasAcceptedCookies } from "@src/hooks/useCookieConsent"
// Import the generated route tree
import { routeTree } from "./routeTree.gen"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { useAppStore } from "./state"
import { QUERY_CACHE_DB_NAME } from "@src/utils/consts"
import { Error } from "@src/components/atoms/Error"
import { DefaultErrorComponent } from "@src/components/organisms/DefaultErrorComponent"
import { MaintenancePage } from "@src/components/pages/MaintenancePage"
import { shouldRetryRequest } from "@src/api/queryDefaults"

// Set at build time by .github/workflows/maintenance-page.yml. Gating here —
// before the router mounts — is what blocks every URL: no route loaders run,
// no API calls fire, and the auth token + persisted query cache are left
// untouched (a failing getCurrentUser loader would otherwise log the user out
// and wipe the cache, see src/routes/_auth.tsx).
const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === "true"

interface IndexedDBStorage {
  getItem: (key: string) => Promise<string | undefined | null>
  setItem: (key: string, value: unknown) => Promise<unknown>
  removeItem: (key: string) => Promise<void>
}

const createIndexedDBPersister = async (): Promise<
  ReturnType<typeof createAsyncStoragePersister>
> => {
  const db = await openDB(QUERY_CACHE_DB_NAME, 1, {
    upgrade(db) {
      db.createObjectStore("queries")
    },
  })

  const storage: IndexedDBStorage = {
    getItem: async (key) =>
      (await db.get("queries", key)) as string | null | undefined,
    setItem: async (key, value) => await db.put("queries", value, key),
    removeItem: async (key) => await db.delete("queries", key),
  }

  return createAsyncStoragePersister({ storage })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24 * 5, // 5 days
      retry: shouldRetryRequest,
    },
  },
})

export type RouterContext = {
  queryClient: QueryClient
  useAppStore: typeof useAppStore
}

export const memoryHistory = createMemoryHistory({
  initialEntries: ["/"],
})

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    useAppStore,
  },
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  // history: memoryHistory,
  defaultErrorComponent: DefaultErrorComponent,
  defaultNotFoundComponent: () => <Error code={404} />,
})

// Register the router instance for type safety and inference
// See https://tanstack.com/router/v1/docs/framework/react/decisions-on-dx#2-declaring-the-router-instance-for-type-inference
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

if (!isMaintenanceMode) {
  createIndexedDBPersister().then((persister) => {
    persistQueryClient({
      queryClient,
      persister,
    })
  })
}

// When a new deployment invalidates old JS chunks, Vite throws this error.
// Reload once to get the new HTML with updated chunk references.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault()
  window.location.reload()
})

// Mocked API for the visual regression suite and offline development
// (`pnpm dev:mock`). The DEV guard lets the production build drop the worker
// and every *.msw fixture module.
async function enableMocking() {
  if (!import.meta.env.DEV || import.meta.env.VITE_MSW !== "true") return
  const { worker } = await import("./mocks/browser")
  await worker.start({ onUnhandledRequest: "bypass", quiet: true })
}

enableMocking().then(() =>
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <StrictMode>
      {isMaintenanceMode ? (
        <MaintenancePage />
      ) : (
        <PostHogProvider
          apiKey={import.meta.env.VITE_POSTHOG_API_KEY}
          options={{
            api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
            defaults: "2026-01-30",
            capture_exceptions: true,
            opt_out_capturing_by_default: !hasAcceptedCookies(),
            opt_out_persistence_by_default: !hasAcceptedCookies(),
            // Enable this for more detailed debugging and logging
            debug: false,
          }}
        >
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </PostHogProvider>
      )}
    </StrictMode>,
  ),
)
